/**
 * Dashboard analytics-summary request: owned here, not by the component that renders it.
 *
 * ── Why this module exists ────────────────────────────────────────────────────
 *
 * `GET /shop-analytics` scopes off the SESSION — `connections.shopify.currentShopId`
 * (api/routes/GET-shop-analytics.js) — and never reads a shop id from the request. So the
 * `shopId` prop threaded into <AnalyticsSummaryCard/> was only ever a readiness gate, as its
 * own comment says. Firing the fetch from that component's mount effect therefore made the
 * request wait on four things it does not need:
 *
 *   1. ShopProvider's shop query          320 ms p50 / 2.1 s p90
 *   2. the dashboard's campaigns query    (`paintGateOpen` in routes/index.jsx)
 *   3. requestIdleCallback(…, 500 ms)
 *   4. the lazy AnalyticsSummaryCard chunk (CDN MISS measured 0.43–1.98 s)
 *
 * …before the server even began its HogQL round trip. Pulling the request out of the
 * component lets routes/index.jsx start it the moment a shop id exists from EITHER the
 * session or the shop record — in parallel with its own campaigns query, which un-pauses on
 * exactly the same signal for exactly the same reason.
 *
 * The component still renders on the deferred tier. Only the NETWORK moved earlier; the
 * first-paint budget is unchanged.
 *
 * ── Keyed by shop id, and that is a tenancy requirement ───────────────────────
 *
 * Not an optimisation. The embedded admin serves every one of a merchant's shops from ONE
 * origin, and switching stores reloads the iframe — which can reuse this JS realm. An
 * unkeyed cache would hand shop B the numbers fetched for shop A. Same reasoning as the
 * `private, no-store` header the route sets (Layer 5 in docs/patterns/SHOP_ISOLATION_SECURITY.md):
 * the URL carries no shop, so anything caching the RESPONSE must supply the key itself.
 *
 * ⚠️ In memory only — deliberately NOT sessionStorage. Module scope survives every
 * client-side navigation in the SPA (dashboard → editor → dashboard, the common case) and
 * dies with the realm, so a merchant's analytics never lands in web storage. A reload
 * re-fetches, which is correct: that is also when the server's own cache is most likely warm.
 *
 * ── Single-flight ─────────────────────────────────────────────────────────────
 *
 * `/shop-analytics` is recorded in docs/architecture/admin-load-time.md as firing TWICE per
 * boot with the cause unidentified (the leading candidates are a remount through
 * components/App.jsx's `loading` branch, which returns <BootShell/> INSTEAD of the whole
 * <EmbeddedApp/> subtree, and `resetClients()` re-executing operations). Holding one promise
 * per shop id collapses concurrent callers onto one request whichever it turns out to be —
 * this de-duplicates rather than suppresses, so it cannot mask a genuine second need.
 */

/**
 * Matches QUERY_CACHE_TTL in api/helpers/posthogInsightsManager.js.
 *
 * Deliberately the same number so the two layers are reasoned about as one.
 *
 * ⚠️ It does NOT mean a client entry is never staler than a fresh request would be — the two
 * TTLs are independent and COMPOUND. An entry created from a server response that was already
 * 299 s old, read at +299 s, is ~10 minutes stale where a fresh request would have returned
 * data at most 5 minutes old. That is the real worst case; it is accepted because these are
 * 30-day totals, where ten minutes cannot move a number visibly, and because the Refresh
 * button forces both layers (`force` drops this entry AND sends `nocache=true`).
 * Raising this past the server's TTL would widen that window with nothing to bound it.
 */
const TTL_MS = 5 * 60 * 1000;

/**
 * Much shorter TTL for a response the server flagged as DEMO.
 *
 * ⚠️ THIS IS A CORRECTNESS BOUND, NOT A TUNING KNOB. `GET /shop-analytics` answers a request
 * that arrives with NO shop context as **HTTP 200, `success: true`**, carrying fabricated
 * figures (29,650 impressions / 247 conversions / $18,450) with `isDemo: true`. That is a
 * plausible answer, not an error, so nothing downstream can reject it.
 *
 * The prefetch is a raw cookie-authenticated `fetch`, NOT `api.fetch` — it does not carry the
 * App Bridge `ShopifySessionToken` the GraphQL connection uses. So a settled
 * `api.currentSession` query does NOT prove the cookie session is shop-attached, and starting
 * the request the instant `session.shopId` exists widens the window where it is not
 * (third-party-cookie restrictions inside the embedded iframe are the standing case).
 *
 * Before this module, every card mount re-fetched, so a wrong demo answer was transient. A
 * full-TTL cache would pin "Sample data" over a real merchant's dashboard for five minutes,
 * surviving remounts and SPA navigation, escapable only via Refresh. 30 s keeps the
 * prefetch→mount de-duplication that is the whole point (that gap is 1-4 s) while bounding the
 * damage to about one screen-glance. A genuinely empty shop just re-requests sooner, which
 * costs one ~470 ms call and is what happened before this module existed.
 */
const DEMO_TTL_MS = 30 * 1000;

/**
 * `summary=true` is what makes this cheap server-side — it skips funnel, devices, locations
 * and the 3-query attribution block. Keep it in sync with what the card actually reads
 * (overview + comparison).
 */
const SUMMARY_QUERY = 'timeframe=30d&insights=trends&summary=true';

/**
 * shopId → { promise, value, ts, ttl } — `value` present only once the promise has fulfilled.
 * `ttl` is per-entry because a demo answer expires far sooner than a real one (see DEMO_TTL_MS).
 */
const entries = new Map();

/**
 * Normalise the "these numbers are fabricated" signal into ONE boolean.
 *
 * ⚠️ The route reports it in two different places depending on which branch answered:
 * `isDemo` at the TOP level of the envelope, and `isUsingMockData` INSIDE `data`. The card
 * used to read `data.isDemo || data.isMockData` — neither of which the route has ever set —
 * so the "Demo Data" badge and the sample-data banner could not render, and mock numbers
 * (247 conversions, $18,450 revenue) presented as real. Read every shape the route can emit.
 */
function isDemoResponse(json) {
  return (
    json?.isDemo === true ||
    json?.data?.isDemo === true ||
    json?.data?.isMockData === true ||
    json?.data?.isUsingMockData === true
  );
}

/**
 * Ceiling on a single request, so "slow" can never become "forever".
 *
 * ⚠️ THIS IS A TERMINAL-STATE GUARANTEE, NOT A PERFORMANCE KNOB. `fetch` never times out on
 * its own. Without this, a request that never settles pins <AnalyticsSummaryCard/> on its
 * skeleton permanently — `loading` is cleared only in that component's `finally`, and its
 * SHOP_WATCHDOG_MS covers the *other* hang (no `shopId` ever arrives), not this one. Single
 * flight sharpens it: the card awaits the PREFETCH's promise, so one hung prefetch hangs the
 * card too. "Still loading" and "will never load" are pixel-identical — the failure mode this
 * repo keeps re-learning (.claude/rules/frontend.md).
 *
 * ⚠️ 60s is deliberately LONGER than it feels like it should be. Measured against production
 * 2026-08-18, a cold Gadget route worker answered in 14,958 ms and 40,690 ms while the warm
 * median was 470 ms. A 20-30s ceiling would abort a request that was going to SUCCEED and
 * replace real numbers with the demo fallback — worse than waiting. The job here is to bound
 * an unbounded wait, not to police a slow one.
 */
const REQUEST_TIMEOUT_MS = 60000;

async function requestSummary(force) {
  // `nocache=true` bypasses the SERVER's PostHog cache; the client entry is dropped by the
  // caller. Both halves are needed for the refresh button to mean anything.
  const url = `/shop-analytics?${SUMMARY_QUERY}${force ? '&nocache=true' : ''}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    return await performRequest(url, controller.signal);
  } catch (error) {
    // Translate the abort into something a merchant-facing banner can say. `AbortError` on
    // its own reads as "something cancelled this", which is not what happened.
    if (error?.name === 'AbortError') {
      throw new Error('Analytics timed out. Try refreshing in a moment.');
    }
    throw error;
  } finally {
    // Runs on success, failure and abort alike — a leaked timer would hold the callback (and
    // this closure) alive for a minute after the request it belongs to is long gone.
    clearTimeout(timeoutId);
  }
}

async function performRequest(url, signal) {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    signal,
    // Pairs with the route's own `Cache-Control: private, no-store, must-revalidate`.
    // The request line carries NO shop identifier — the shop is resolved server-side from
    // the session — so the HTTP cache would key two different shops' responses to the same
    // URL. .claude/rules/backend.md calls this Layer 5, and notes the two halves must move
    // together. components/AnalyticsDashboard.jsx sets the same flag for the same reason.
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    // Merchant-facing: this reaches the banner verbatim via setError(err.message). The status
    // is kept because it is the one thing that makes a support conversation short, but the
    // sentence has to survive being read by someone who is not a developer — and it is
    // concatenated with more copy in the card, so it must end in a full stop.
    throw new Error(`Analytics couldn't be loaded (error ${response.status}).`);
  }

  const json = await response.json();

  if (!json?.success) {
    throw new Error(json?.message || 'Failed to load analytics');
  }

  if (!json.data) {
    // `success: true` with no `data` would sail through the card's success path and paint
    // 0 / 0% / 0 / $0 with no banner — indistinguishable from a real quiet shop. The route
    // always sends `data`, so this is a guard against a truncated or proxied response, not
    // against the route.
    throw new Error("Analytics returned an empty response. Try refreshing in a moment.");
  }

  return { data: json.data, isDemo: isDemoResponse(json) };
}

/**
 * Fetch (or reuse) the dashboard analytics summary for one shop.
 *
 * @param {string|undefined|null} shopId - `shop?.id ?? session?.shopId`. Falsy is a no-op
 *   that rejects, so callers keep their existing readiness guard rather than firing a
 *   request the server would answer with mock data.
 * @param {{force?: boolean}} [options] - `force` skips both caches (the Refresh button).
 * @returns {Promise<{data: object, isDemo: boolean}>}
 */
export function fetchAnalyticsSummary(shopId, { force = false } = {}) {
  if (!shopId) {
    return Promise.reject(new Error('fetchAnalyticsSummary requires a shop id'));
  }

  const key = String(shopId);

  if (force) {
    entries.delete(key);
  } else {
    const entry = entries.get(key);
    if (entry && Date.now() - entry.ts < entry.ttl) {
      return entry.promise;
    }
  }

  const promise = requestSummary(force).then(
    (value) => {
      // Refresh the timestamp on SETTLE, not on start: a slow request should not have
      // already burned part of its own TTL by the time anyone can read it.
      const entry = entries.get(key);
      if (entry && entry.promise === promise) {
        entry.value = value;
        entry.ts = Date.now();
        // Demote a demo answer's lifetime the moment we can see it is one. Set on SETTLE
        // because the flag only exists in the response — an in-flight entry has to keep the
        // full window so concurrent callers still de-duplicate onto it.
        entry.ttl = value.isDemo ? DEMO_TTL_MS : TTL_MS;
      }
      return value;
    },
    (error) => {
      // A failure must not be cached — the next caller has to be able to retry. Only drop
      // OUR entry, so a newer in-flight request (from Refresh) is left alone.
      const entry = entries.get(key);
      if (entry && entry.promise === promise) entries.delete(key);
      throw error;
    }
  );

  entries.set(key, { promise, value: null, ts: Date.now(), ttl: TTL_MS });
  return promise;
}

/**
 * The already-settled value for a shop, or null.
 *
 * Lets a component paint on its FIRST render when the prefetch has already landed, instead
 * of showing a skeleton for one frame and then swapping it — which is the whole point of
 * starting the request early.
 */
export function readAnalyticsSummary(shopId) {
  if (!shopId) return null;
  const entry = entries.get(String(shopId));
  if (!entry || !entry.value) return null;
  if (Date.now() - entry.ts >= entry.ttl) return null;
  return entry.value;
}
